/**
 * yt-shorts.js — YouTube Shorts Shoppable Carousel
 * Production v1.0
 *
 * Fixes in this version:
 *  1. Modal arrows — placed on .yt-modal-outer (overflow:visible), never clipped
 *  2. Add to Cart — re-fetches /cart.js and updates ALL theme cart count selectors
 *     live without page reload
 *  3. Responsive — mobile bottom-sheet, tablet narrow-video, desktop side-by-side
 */
(function () {
  'use strict';

  /* ── Boot ── */
  function init() {
    document.querySelectorAll('[data-section-id]').forEach(function (section) {
      if (!section.id || !section.id.startsWith('yt-shorts-')) return;
      var sid = section.dataset.sectionId;
      if (!sid || section._ytCarousel) return;      // prevent double-init
      var el = document.getElementById('yt-data-' + sid);
      if (!el) return;
      var data;
      try { data = JSON.parse(el.textContent); }
      catch (e) { console.warn('[YT Shorts] JSON parse error', e); return; }
      section._ytCarousel = new YTShorts(section, data);
    });
  }

  /* ================================================================
     YTShorts — one instance per section
  ================================================================ */
  function YTShorts(section, data) {
    this.section      = section;
    this.products     = data.products || [];
    this.sid          = data.sectionId;
    this.activeIndex  = 0;

    this.track        = section.querySelector('#yt-track-'        + this.sid);
    this.dotsWrap     = section.querySelector('#yt-dots-'         + this.sid);
    this.overlay      = section.querySelector('#yt-modal-'        + this.sid);
    this.iframeWrap   = section.querySelector('#yt-iframe-wrap-'  + this.sid);
    this.productPanel = section.querySelector('#yt-product-panel-'+ this.sid);
    this.prevBtn      = section.querySelector('.yt-prev');
    this.nextBtn      = section.querySelector('.yt-next');

    if (!this.track || !this.overlay) return;

    // Modal outer holds the arrows (not .yt-modal — which has overflow:hidden)
    this.modalOuter = this.overlay.querySelector('.yt-modal-outer');
    this.arrowPrev  = this.modalOuter && this.modalOuter.querySelector('.yt-modal-prev');
    this.arrowNext  = this.modalOuter && this.modalOuter.querySelector('.yt-modal-next');

    this._setupCarousel();
    this._setupCards();
    this._setupModal();
    this._setupDrag();
    this._setupLazyLoad();
    this._setupDots();
  }

  /* ── Carousel nav ── */
  YTShorts.prototype._setupCarousel = function () {
    var self = this;
    var track = this.track;

    function nudge(dir) {
      var cardW = (track.querySelector('.yt-card') || {}).offsetWidth || 210;
      track.scrollBy({ left: dir * (cardW + 14) * 2, behavior: 'smooth' });
    }

    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { nudge(-1); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { nudge(1); });

    track.addEventListener('scroll', function () {
      self._syncCarouselArrows();
      self._syncDots();
    }, { passive: true });

    this._syncCarouselArrows();
  };

  YTShorts.prototype._syncCarouselArrows = function () {
    var t = this.track;
    var atStart = t.scrollLeft < 8;
    var atEnd   = t.scrollLeft + t.clientWidth >= t.scrollWidth - 8;
    if (this.prevBtn) this.prevBtn.classList.toggle('is-hidden', atStart);
    if (this.nextBtn) this.nextBtn.classList.toggle('is-hidden', atEnd);
  };

  /* ── Card click → open modal ── */
  YTShorts.prototype._setupCards = function () {
    var self = this;
    this.track.addEventListener('click', function (e) {
      var btn = e.target.closest('.yt-card-btn');
      if (!btn) return;
      var pid = parseInt(btn.dataset.productId, 10);
      var vid = btn.dataset.videoId;
      // Match on both product id AND video id (one product can have multiple shorts)
      var idx = self.products.findIndex(function (p) { return p.id === pid && p.videoId === vid; });
      if (idx < 0) idx = self.products.findIndex(function (p) { return p.id === pid; });
      if (idx < 0) return;
      self._openModal(idx);
    });
  };

  /* ── Modal open ── */
  YTShorts.prototype._openModal = function (idx) {
    this.activeIndex = idx;
    this._renderModal();
    this.overlay.removeAttribute('aria-hidden');
    this.overlay.classList.add('is-open');
    document.body.classList.add('yt-modal-open');
    var closeBtn = this.overlay.querySelector('.yt-modal-close');
    if (closeBtn) closeBtn.focus();
  };

  /* ── Modal close ── */
  YTShorts.prototype._closeModal = function () {
    this.overlay.setAttribute('aria-hidden', 'true');
    this.overlay.classList.remove('is-open');
    document.body.classList.remove('yt-modal-open');
    if (this.iframeWrap)   this.iframeWrap.innerHTML   = '';
    if (this.productPanel) this.productPanel.innerHTML  = '';
  };

  /* ── Render modal content ── */
  YTShorts.prototype._renderModal = function () {
    var p = this.products[this.activeIndex];
    if (!p) return;

    /* Autoplay iframe — injected only on open (performance) */
    if (this.iframeWrap) {
      var src = 'https://www.youtube.com/embed/' + p.videoId
        + '?autoplay=1&loop=1&playlist=' + p.videoId
        + '&rel=0&modestbranding=1&playsinline=1';
      this.iframeWrap.innerHTML =
        '<iframe src="' + src + '" title="' + _esc(p.title) + '"'
        + ' frameborder="0" allow="accelerometer; autoplay; clipboard-write;'
        + ' encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';
    }

    /* Product panel */
    if (this.productPanel) {
      this.productPanel.innerHTML = this._buildProductHTML(p);
      this._bindTabs(this.productPanel);
      this._bindVariants(this.productPanel);
      this._bindATC(this.productPanel, p);
    }

    /* Modal arrows */
    var hasPrev = this.activeIndex > 0;
    var hasNext = this.activeIndex < this.products.length - 1;
    if (this.arrowPrev) this.arrowPrev.classList.toggle('is-hidden', !hasPrev);
    if (this.arrowNext) this.arrowNext.classList.toggle('is-hidden', !hasNext);
  };

  /* ── Product panel HTML ── */
  YTShorts.prototype._buildProductHTML = function (p) {
    /* Discount % */
    var disc = (p.comparePriceRaw && p.priceRaw && p.comparePriceRaw > p.priceRaw)
      ? Math.round((1 - p.priceRaw / p.comparePriceRaw) * 100) : 0;

    var imgBadge    = p.comparePrice ? '<span class="yt-sale-badge">Sale</span>' : '';
    var compareHTML = p.comparePrice
      ? '<s class="yt-modal-compare">' + p.comparePrice + '</s>'
        + (disc > 0 ? '<span class="yt-save-badge">Save ' + disc + '%</span>' : '')
      : '';

    /* Tabs — only render if content exists */
    var tabs = [
      { id: 'desc',    label: 'Description', body: p.description || '' },
      { id: 'ingr',    label: 'Ingredients', body: p.ingredients || '' },
      { id: 'howtake', label: 'How to Take',  body: p.howToTake  || '' }
    ].filter(function (t) { return t.body.trim() !== ''; });

    var tabNav = tabs.map(function (t, i) {
      return '<button class="yt-tab-btn' + (i === 0 ? ' is-active' : '')
        + '" data-tab="' + t.id + '">' + t.label + '</button>';
    }).join('');

    var tabPanels = tabs.map(function (t, i) {
      return '<div class="yt-tab-panel' + (i === 0 ? ' is-active' : '')
        + '" data-panel="' + t.id + '">' + t.body + '</div>';
    }).join('');

    /* Variants */
    var variantsHTML = '';
    if (p.variants && p.variants.length > 1) {
      variantsHTML = '<div class="yt-variants"><p class="yt-variants-label">Pack:</p>'
        + '<div class="yt-variants-list">'
        + p.variants.map(function (v, i) {
            return '<button class="yt-variant-btn' + (i === 0 ? ' is-active' : '')
              + '" data-variant-id="' + v.id + '"'
              + (v.available ? '' : ' disabled aria-disabled="true"')
              + '>' + _esc(v.title) + '</button>';
          }).join('')
        + '</div></div>';
    }

    var available  = p.available !== false;
    var atcLabel   = available ? 'Add to Cart' : 'Sold Out';
    var atcDisabled = available ? '' : ' disabled aria-disabled="true"';
    var cartIcon   = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">'
      + '<path d="M2 2h2l2.5 9h7l1.5-6H6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>'
      + '<circle cx="8" cy="14.5" r="1.2" fill="currentColor"/>'
      + '<circle cx="12.5" cy="14.5" r="1.2" fill="currentColor"/></svg>';

    return ''
      + '<div class="yt-modal-product-inner">'

        /* Image — hidden on mobile via CSS */
        + '<div class="yt-modal-img-wrap">'
          + '<img src="' + p.image + '" alt="' + _esc(p.imageAlt) + '" width="280" height="280" loading="lazy">'
          + imgBadge
        + '</div>'

        + '<div class="yt-modal-product-info">'
          + '<p class="yt-modal-product-title">' + _esc(p.title) + '</p>'

          + '<div class="yt-modal-price-row">'
            + '<span class="yt-modal-price">' + p.price + '</span>'
            + compareHTML
          + '</div>'

          + variantsHTML

          + (tabs.length > 0
            ? '<div class="yt-tabs">'
                + '<div class="yt-tab-nav">' + tabNav + '</div>'
                + '<div class="yt-tab-body">' + tabPanels + '</div>'
              + '</div>'
            : '')

          + '<div class="yt-modal-actions">'
            + '<a class="yt-more-info-btn" href="' + p.url + '">More Info</a>'
            + '<button class="yt-atc-btn" data-variant-id="' + p.variantId + '"' + atcDisabled + '>'
              + atcLabel + cartIcon
            + '</button>'
          + '</div>'

        + '</div>'

      + '</div>';
  };

  /* ── Tabs ── */
  YTShorts.prototype._bindTabs = function (panel) {
    panel.addEventListener('click', function (e) {
      var btn = e.target.closest('.yt-tab-btn');
      if (!btn) return;
      panel.querySelectorAll('.yt-tab-btn').forEach(function (b) { b.classList.remove('is-active'); });
      panel.querySelectorAll('.yt-tab-panel').forEach(function (p) { p.classList.remove('is-active'); });
      btn.classList.add('is-active');
      var pane = panel.querySelector('[data-panel="' + btn.dataset.tab + '"]');
      if (pane) pane.classList.add('is-active');
    });
  };

  /* ── Variant select ── */
  YTShorts.prototype._bindVariants = function (panel) {
    panel.addEventListener('click', function (e) {
      var btn = e.target.closest('.yt-variant-btn');
      if (!btn || btn.disabled) return;
      panel.querySelectorAll('.yt-variant-btn').forEach(function (b) { b.classList.remove('is-active'); });
      btn.classList.add('is-active');
      var atc = panel.querySelector('.yt-atc-btn');
      if (atc) atc.dataset.variantId = btn.dataset.variantId;
    });
  };

  /* ── Add to Cart — live cart update, no page reload ──
     ROOT CAUSE FIX: Dawn (and most OS 2.0 themes) render the cart icon
     bubble through the Section Rendering API. The bubble's real markup
     looks like:
       <div class="cart-count-bubble" hidden>...</div>
     A plain textContent swap never removes that `hidden` attribute, and
     theme JS (cart.js → CartNotification / CartDrawer) never even sees
     our fetch call, so it doesn't re-render. That's why the number only
     updates after a full page refresh (which re-runs the Liquid).

     FIX: after a successful /cart/add.js call, request the SAME header
     section HTML via the Section Rendering API
       /?sections=<header-section-id>
     parse out the real cart-count node from that markup, and swap it
     into the live DOM. This mirrors exactly what Shopify does on a
     full reload, so it always matches truth — no guessing selectors.
     A generic selector-based fallback still runs underneath for
     custom/non-Dawn themes.
  ── */
  YTShorts.prototype._bindATC = function (panel, product) {
    panel.addEventListener('click', function (e) {
      var btn = e.target.closest('.yt-atc-btn');
      if (!btn || btn.disabled) return;

      var variantId = parseInt(btn.dataset.variantId, 10);
      if (!variantId) return;

      /* Loading state */
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.textContent = 'Adding…';

      /* Step 1 — add to cart, requesting pre-rendered section HTML in the
         SAME call. This is the correct Shopify pattern: /cart/add.js
         accepts a "sections" array in its POST body and returns a
         "sections" key in the response containing fresh HTML for each
         requested section — exactly what <cart-drawer>.renderContents()
         needs to repaint itself. A separate GET /cart.js does NOT support
         ?sections= (that's add.js / update.js / change.js only), which is
         why the earlier version fetched empty drawer content. */
      var sectionIds = ['cart-drawer', 'cart-icon-bubble'];

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: sectionIds,
          sections_url: window.location.pathname
        })
      })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw new Error(d.description || 'Add failed'); });
        return r.json();
      })
      .then(function (addResponse) {
        /* addResponse.sections (if present) already has fresh cart-drawer
           + cart-icon-bubble HTML — use it directly, no extra fetch needed. */
        if (addResponse && addResponse.sections) {
          _renderSectionsFromAddResponse(addResponse.sections);
        }
        /* Still fetch /cart.js for the plain item_count used by the
           generic fallback selectors (Layer 2) */
        return fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* Step 2 — sync cart bubble count + generic fallback selectors.
           Drawer itself was already repainted above from addResponse.sections. */
        _syncCartUI(cart);

        /* Success state */
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        btn.textContent = '✓ Added!';

        setTimeout(function () {
          btn.classList.remove('is-added');
          btn.disabled = false;
          btn.innerHTML = 'Add to Cart'
            + '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">'
            + '<path d="M2 2h2l2.5 9h7l1.5-6H6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>'
            + '<circle cx="8" cy="14.5" r="1.2" fill="currentColor"/>'
            + '<circle cx="12.5" cy="14.5" r="1.2" fill="currentColor"/></svg>';
        }, 2600);

        /* Fire events themes already listen to (Dawn's cart.js listens for this) */
        document.dispatchEvent(new CustomEvent('cart:refresh',  { bubbles: true, detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:updated',  { bubbles: true, detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:build',    { bubbles: true, detail: { cart: cart } }));
      })
      .catch(function (err) {
        console.warn('[YT Shorts] ATC error:', err);
        btn.disabled = false;
        btn.classList.remove('is-loading');
        btn.textContent = 'Retry';
      });
    });
  };

  /**
   * _renderSectionsFromAddResponse — takes the "sections" object returned
   * directly by /cart/add.js (when we pass sections:[...] in the request)
   * and swaps the fresh HTML into the live DOM. This is the single most
   * reliable way to repaint Dawn's cart-drawer because it's the exact same
   * response Dawn's own theme JS (global.js → onCartUpdate) would use.
   */
  function _renderSectionsFromAddResponse(sections) {
    if (!sections) return;
    var parser = new DOMParser();

    Object.keys(sections).forEach(function (sectionId) {
      var html = sections[sectionId];
      if (!html) return;
      var doc = parser.parseFromString(html, 'text/html');

      if (sectionId === 'cart-drawer') {
        var freshDrawer = doc.querySelector('cart-drawer');
        var liveDrawer  = document.querySelector('cart-drawer');
        if (freshDrawer && liveDrawer && liveDrawer.parentNode) {
          liveDrawer.parentNode.replaceChild(freshDrawer, liveDrawer);
          /* Dawn's <cart-drawer> opens itself via a CSS class toggle —
             replicate that so the now-populated drawer is visible */
          var newLiveDrawer = document.querySelector('cart-drawer');
          if (newLiveDrawer && typeof newLiveDrawer.open === 'function') {
            try { newLiveDrawer.open(); } catch (e) {}
          } else if (newLiveDrawer) {
            newLiveDrawer.classList.add('active', 'drawer--is-open');
            document.body.classList.add('overflow-hidden');
          }
        }
      } else if (sectionId === 'cart-icon-bubble') {
        var freshBubble = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
        var liveBubbleWrap = document.getElementById('cart-icon-bubble');
        if (freshBubble && liveBubbleWrap && liveBubbleWrap.parentNode) {
          /* Replace inner HTML of the wrapper, keeping the wrapper itself
             so any existing JS references to #cart-icon-bubble stay valid */
          liveBubbleWrap.innerHTML = freshBubble.innerHTML || html;
          liveBubbleWrap.classList.add('yt-cart-pop');
        }
      }
    });
  }

  /**
   * _syncCartUI — the single source of truth for "did the cart icon update".
   * Two layers, both run every time:
   *
   * LAYER 1 (primary) — Section Rendering API.
   *   Shopify can return any section's HTML pre-rendered with the LATEST
   *   cart state, without a full page load:
   *     GET /?sections=SECTION_ID
   *   We try a list of section IDs that ship with Dawn/most OS 2.0 themes
   *   ("header", "cart-icon-bubble", etc). Whichever responds, we lift the
   *   .cart-count-bubble (or equivalent) node out of the returned HTML and
   *   replace the live one. This guarantees the *exact* markup Shopify
   *   would render on refresh — including removing `hidden` attributes —
   *   because it IS that markup.
   *
   * LAYER 2 (fallback) — generic selector text-swap, for custom themes
   *   that don't use the Sections API for their cart icon.
   */
  function _syncCartUI(cart) {
    var count = cart.item_count || 0;

    /* NOTE: cart-drawer + cart-icon-bubble are already repainted directly
       from the /cart/add.js response by _renderSectionsFromAddResponse(),
       which runs BEFORE this function is called. That is the primary,
       most reliable path. Everything below is extra insurance:
       Layer 1 re-fetches the bubble/header via the Section Rendering API
       in case the theme's section IDs differ from what add.js returned,
       and Layer 2 is a plain text-swap fallback for non-Dawn themes. */

    /* ---------- LAYER 1: Section Rendering API (cart bubble / header) ---------- */
    var candidateSectionIds = [
      'cart-icon-bubble',   // Dawn's dedicated cart bubble section
      'header'              // Most themes' main header section
    ];

    var triedAny = false;

    candidateSectionIds.forEach(function (sectionId) {
      var el = document.getElementById(sectionId) || document.querySelector('[data-section-id="' + sectionId + '"]') || document.querySelector('.shopify-section-' + sectionId) || document.querySelector('#shopify-section-' + sectionId);
      // Only bother calling the API if that section actually exists on this page
      if (!el && sectionId !== 'cart-icon-bubble') return;

      triedAny = true;
      fetch('/?sections=' + sectionId)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (json) {
          if (!json || !json[sectionId]) return;
          var html = json[sectionId];
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');

          /* Try to find the cart bubble inside the returned section HTML */
          var freshBubble = doc.getElementById('cart-icon-bubble')
            || doc.querySelector('.cart-count-bubble')
            || doc.querySelector('[data-cart-count]');

          if (!freshBubble) return;

          /* Replace the matching live element with the freshly rendered one */
          var liveTarget = document.getElementById(freshBubble.id)
            || document.querySelector('.cart-count-bubble')
            || document.querySelector('[data-cart-count]');

          if (liveTarget && liveTarget.parentNode) {
            liveTarget.parentNode.replaceChild(freshBubble, liveTarget);
            freshBubble.classList.add('yt-cart-pop');
          }
        })
        .catch(function () { /* section not found on this theme — silently ignore */ });
    });

    /* ---------- LAYER 2: generic fallback (always runs too) ---------- */
    _updateCartCountGeneric(count);

    return triedAny;
  }

  /**
   * _refreshDawnCartDrawerSection — fallback when cart-drawer.renderContents()
   * is unavailable (e.g. older Dawn version or a custom build). Fetches the
   * actual cart-drawer section HTML via the Section Rendering API and
   * replaces the drawer's inner content wholesale — same end result.
   */
  function _refreshDawnCartDrawerSection() {
    fetch('/?sections=cart-drawer')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) {
        if (!json || !json['cart-drawer']) return;
        var parser = new DOMParser();
        var doc = parser.parseFromString(json['cart-drawer'], 'text/html');
        var freshDrawer = doc.querySelector('cart-drawer');
        var liveDrawer  = document.querySelector('cart-drawer');
        if (freshDrawer && liveDrawer && liveDrawer.parentNode) {
          liveDrawer.parentNode.replaceChild(freshDrawer, liveDrawer);
        }
      })
      .catch(function () { /* not a Dawn-based theme — ignore */ });
  }

  /**
   * _updateCartCountGeneric — text-swap fallback for themes that render
   * their cart count as a plain number node (no hidden-attribute pattern).
   * Runs in addition to Layer 1 so nothing regresses for non-Dawn stores.
   */
  function _updateCartCountGeneric(count) {
    var selectors = [
      '.cart-count-bubble span:not(.visually-hidden)',
      '.cart-count',
      '.cart__count',
      '.cart-badge',
      '.cart_count',
      '.CartCount',
      '.header__cart-count',
      '.site-header__cart-count',
      '.js-cart-count',
      '.cart-item-count',
      '[data-cart-count]',
      '[data-cart-item-count]'
    ];

    selectors.forEach(function (sel) {
      try {
        document.querySelectorAll(sel).forEach(function (el) {
          /* Skip elements that were just replaced by Layer 1's fresh markup
             — those already carry the correct value, re-stamping the text
             here is harmless but the hidden-attribute logic below matters
             for plain custom themes. */
          el.textContent = count;
          el.setAttribute('data-count', count);
          el.setAttribute('data-cart-count', count);

          if (count > 0) {
            el.removeAttribute('hidden');
            el.style.display = '';
            el.classList.remove('hidden', 'is-hidden', 'hide', 'invisible');

            /* Walk up to a wrapper that might itself carry `hidden`
               (Dawn wraps the whole bubble, not just the number span) */
            var bubble = el.closest('.cart-count-bubble');
            if (bubble) bubble.removeAttribute('hidden');
          }

          el.classList.remove('yt-cart-pop');
          void el.offsetWidth;
          el.classList.add('yt-cart-pop');
        });
      } catch (_) {}
    });
  }

  /* ── Modal setup: close + arrows + keyboard ── */
  YTShorts.prototype._setupModal = function () {
    var self    = this;
    var overlay = this.overlay;

    /* Close button */
    var closeBtn = overlay.querySelector('.yt-modal-close');
    if (closeBtn) closeBtn.addEventListener('click', function () { self._closeModal(); });

    /* Click backdrop */
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) self._closeModal();
    });

    /* Prev / Next arrows */
    if (this.arrowPrev) {
      this.arrowPrev.addEventListener('click', function () {
        if (self.activeIndex > 0) self._openModal(self.activeIndex - 1);
      });
    }
    if (this.arrowNext) {
      this.arrowNext.addEventListener('click', function () {
        if (self.activeIndex < self.products.length - 1) self._openModal(self.activeIndex + 1);
      });
    }

    /* Keyboard */
    document.addEventListener('keydown', function (e) {
      if (!overlay.classList.contains('is-open')) return;
      if (e.key === 'Escape')      self._closeModal();
      if (e.key === 'ArrowLeft'  && self.activeIndex > 0)                         self._openModal(self.activeIndex - 1);
      if (e.key === 'ArrowRight' && self.activeIndex < self.products.length - 1)  self._openModal(self.activeIndex + 1);
    });
  };

  /* ── Drag / swipe on track ── */
  YTShorts.prototype._setupDrag = function () {
    var track = this.track;
    var startX = 0, scrollStart = 0, dragging = false;

    track.addEventListener('mousedown', function (e) {
      dragging = true; startX = e.pageX; scrollStart = track.scrollLeft;
      track.classList.add('is-dragging');
    });
    document.addEventListener('mousemove', function (e) {
      if (!dragging) return;
      track.scrollLeft = scrollStart - (e.pageX - startX);
    });
    document.addEventListener('mouseup', function () {
      dragging = false; track.classList.remove('is-dragging');
    });
    track.addEventListener('touchstart', function (e) {
      startX = e.touches[0].pageX; scrollStart = track.scrollLeft;
    }, { passive: true });
    track.addEventListener('touchmove', function (e) {
      track.scrollLeft = scrollStart - (e.touches[0].pageX - startX);
    }, { passive: true });
  };

  /* ── Lazy-load thumbnails via IntersectionObserver ── */
  YTShorts.prototype._setupLazyLoad = function () {
    var imgs = this.track.querySelectorAll('.yt-thumb[data-src]');
    if (!imgs.length) return;

    function loadImg(img) {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
      img.addEventListener('load', function () {
        img.classList.add('is-loaded');
        /* Hide placeholder fallback */
        var fb = img.closest('.yt-thumb-wrap') && img.closest('.yt-thumb-wrap').querySelector('.yt-thumb-fallback');
        if (fb) fb.style.display = 'none';
      });
      img.addEventListener('error', function () {
        /* maxresdefault failed → try sddefault */
        var sd = img.dataset.srcFallback;
        if (sd && img.src !== sd) {
          img.src = sd;
        } else {
          /* Both failed → show product image fallback */
          img.style.display = 'none';
          var fb = img.closest('.yt-thumb-wrap') && img.closest('.yt-thumb-wrap').querySelector('.yt-thumb-fallback');
          if (fb) fb.classList.add('is-visible');
        }
      });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          loadImg(entry.target);
          io.unobserve(entry.target);
        });
      }, { rootMargin: '300px' });
      imgs.forEach(function (img) { io.observe(img); });
    } else {
      imgs.forEach(loadImg);
    }
  };

  /* ── Dots ── */
  YTShorts.prototype._setupDots = function () {
    var self  = this;
    var track = this.track;
    var wrap  = this.dotsWrap;
    if (!wrap) return;

    wrap.querySelectorAll('.yt-dot').forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        var card = track.querySelectorAll('.yt-card')[i];
        if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      });
    });
  };

  YTShorts.prototype._syncDots = function () {
    var track = this.track;
    var wrap  = this.dotsWrap;
    if (!wrap) return;
    var dots     = wrap.querySelectorAll('.yt-dot');
    var cards    = track.querySelectorAll('.yt-card');
    var rect     = track.getBoundingClientRect();
    var closest  = 0, minDist = Infinity;
    cards.forEach(function (c, i) {
      var d = Math.abs(c.getBoundingClientRect().left - rect.left);
      if (d < minDist) { minDist = d; closest = i; }
    });
    dots.forEach(function (d, i) { d.classList.toggle('is-active', i === closest); });
  };

  /* ── HTML escape ── */
  function _esc(s) {
    return String(s || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ── Init ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  /* Re-init after Theme Customizer section loads */
  document.addEventListener('shopify:section:load', init);

}());